/**
 * Tipos e helpers do histórico de chamadas, compartilhados entre a rota
 * `/api/call-history` (servidor) e o widget (cliente).
 *
 * A API externa (Callix Registro Geral) devolve timestamps em UTC SEM sufixo
 * de fuso ("2026-10-22T19:43:25.099000"). Tudo aqui é convertido para o
 * horário de Brasília (America/Sao_Paulo).
 */
import { formatNationalPhone, onlyDigits } from './phoneUtils';

export const BRASILIA_TIME_ZONE = 'America/Sao_Paulo';

/** Registro bruto devolvido pela API Callix Registro Geral (`/calls`). */
export interface CallixCallRecord {
  _id: string;
  started_at: string;
  answered_at?: string | null;
  ended_at?: string | null;
  destination_phone: string;
  /** 'manual' para chamadas manuais; ausente para chamadas de campanha */
  call_type?: string | null;
  /** presente apenas em chamadas não completadas */
  failure_cause?: string | null;
  /** presente apenas em chamadas completadas */
  hangup_cause?: string | null;
  audio_link?: string | null;
  protocol?: number | null;
  service_duration?: number | null;
  qualification_id?: number | null;
  qualification_name?: string | null;
  note?: string | null;
  campaign_id?: number | string | null;
  campaign_name?: string | null;
  campaign_contact_id?: number | string | null;
  contact_label?: string | null;
  customer_id?: number | null;
  customer_name?: string | null;
  queue_id?: number | null;
  queue_name?: string | null;
  agent_id?: number | null;
  agent_name?: string | null;
  transfer_agent_id?: number | null;
  transfer_agent_name?: string | null;
  user_callix?: {
    login?: string;
    email?: string;
    username_identify?: string;
  } | null;
  /** Campos do formulário do contato (nome do campo -> valor) */
  fields?: Record<string, string> | null;
}

export interface CallixCallsResponse {
  total: number;
  items: CallixCallRecord[];
}

export type CallHistoryType = 'manual' | 'campaign' | 'receptive';

/** Registro formatado entregue ao widget. */
export interface CallHistoryItem {
  id: string;
  phoneNumber: string;
  /** Número no padrão nacional: (51) 3248-8509 */
  formattedPhone: string;
  callType: CallHistoryType;
  callTypeLabel: string;
  callDirection: 'outgoing' | 'incoming';
  wasAnswered: boolean;
  failureCause?: string;
  failureCauseLabel?: string;
  hangupCause?: string;
  hangupCauseLabel?: string;
  /** ISO 8601 em UTC (com Z) */
  startedAt: string;
  answeredAt?: string;
  endedAt?: string;
  /** dd/mm/aaaa em Brasília */
  formattedDate: string;
  /** HH:mm em Brasília */
  formattedTime: string;
  /** HH:mm:ss em Brasília */
  formattedTimeWithSeconds: string;
  /** aaaa-mm-dd em Brasília (para "Hoje"/"Ontem") */
  dateKey: string;
  /** Duração em segundos: conversa (atendida) ou tempo de tentativa (não atendida) */
  duration?: number;
  formattedDuration: string;
  protocol?: number;
  campaignId?: number;
  campaignName?: string;
  campaignContactId?: number;
  contactLabel?: string;
  qualificationId?: number;
  qualificationName?: string;
  note?: string;
  /** Caminho da gravação no Callix (ex.: /api/v1/outgoing_completed_calls/123/audio) */
  audioPath?: string;
  hasAudio: boolean;
  /** Assinatura emitida por /api/call-history, exigida por /api/call-audio */
  /** Prova (HMAC) de que este agente pode abrir o formulário do contato em /api/campaign-form. */
  formSignature?: string;
  formExpiresAt?: number;
  audioSignature?: string;
  /** Expiração da assinatura (unix, segundos) */
  audioExpiresAt?: number;
  fields?: Record<string, string>;
}

export interface CallHistoryResponse {
  total: number;
  items: CallHistoryItem[];
}

/**
 * Converte o timestamp da API para Date. Sem sufixo de fuso ele é UTC
 * (a API grava em UTC); com sufixo (Z ou ±hh:mm) respeita o sufixo.
 */
export function parseCallixTimestamp(value: string | null | undefined): Date | null {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  // Normaliza "2026-10-22 19:43:25" para ISO e microssegundos para milissegundos.
  let iso = trimmed.replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');
  if (!hasZone) {
    iso = `${iso}Z`;
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

type DateParts = { year: string; month: string; day: string; hour: string; minute: string; second: string };

function getPartsInTimeZone(date: Date, timeZone: string): DateParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts: Record<string, string> = {};
  for (const part of formatter.formatToParts(date)) {
    if (part.type !== 'literal') {
      parts[part.type] = part.value;
    }
  }
  // Alguns runtimes devolvem "24" para meia-noite com hour12:false.
  const hour = parts.hour === '24' ? '00' : parts.hour;
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour,
    minute: parts.minute,
    second: parts.second,
  };
}

export function formatDateInBrasilia(date: Date, timeZone = BRASILIA_TIME_ZONE): string {
  const p = getPartsInTimeZone(date, timeZone);
  return `${p.day}/${p.month}/${p.year}`;
}

export function formatTimeInBrasilia(date: Date, withSeconds = false, timeZone = BRASILIA_TIME_ZONE): string {
  const p = getPartsInTimeZone(date, timeZone);
  return withSeconds ? `${p.hour}:${p.minute}:${p.second}` : `${p.hour}:${p.minute}`;
}

/** Chave aaaa-mm-dd no fuso de Brasília. */
export function getDateKeyInBrasilia(date: Date, timeZone = BRASILIA_TIME_ZONE): string {
  const p = getPartsInTimeZone(date, timeZone);
  return `${p.year}-${p.month}-${p.day}`;
}

/** "Hoje", "Ontem" ou dd/mm/aaaa. */
export function describeDay(dateKey: string, formattedDate: string, now: Date = new Date()): string {
  const todayKey = getDateKeyInBrasilia(now);
  if (dateKey === todayKey) {
    return 'Hoje';
  }
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  if (dateKey === getDateKeyInBrasilia(yesterday)) {
    return 'Ontem';
  }
  return formattedDate;
}

export function formatDurationSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) {
    return '--:--';
  }
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(secs).padStart(2, '0');
  return hours > 0 ? `${String(hours).padStart(2, '0')}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Motivos de falha (chamadas não completadas), como no relatório do Callix web. */
export const FAILURE_CAUSE_LABELS: Record<string, string> = {
  canceled: 'Chamada cancelada',
  cancelled: 'Chamada cancelada',
  no_answer: 'Telefone não atendeu',
  noanswer: 'Telefone não atendeu',
  not_answered: 'Telefone não atendeu',
  unanswered: 'Telefone não atendeu',
  timeout: 'Telefone não atendeu',
  busy: 'Telefone ocupado',
  user_busy: 'Telefone ocupado',
  number_not_found: 'Número inexistente',
  invalid_number: 'Número inválido',
  unallocated_number: 'Número inexistente',
  connection_issue: 'Falha de conexão',
  network_error: 'Falha de conexão',
  rejected: 'Chamada rejeitada',
  call_rejected: 'Chamada rejeitada',
  congestion: 'Rede congestionada',
  failed: 'Falha na chamada',
  machine: 'Caixa postal / máquina',
  voicemail: 'Caixa postal',
};

/** Quem desligou (chamadas completadas). */
export const HANGUP_CAUSE_LABELS: Record<string, string> = {
  operator_hangup: 'Agente desligou',
  agent_hangup: 'Agente desligou',
  contact_hangup: 'Contato desligou',
  customer_hangup: 'Contato desligou',
  time_exceeded: 'Tempo excedido',
  timeout: 'Tempo excedido',
  transfer: 'Transferida',
  transferred: 'Transferida',
  system_hangup: 'Encerrada pelo sistema',
  machine_hangup: 'Encerrada pelo sistema',
};

function humanizeCause(cause: string): string {
  const text = cause.replace(/[_-]+/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : cause;
}

export function labelFailureCause(cause: string | null | undefined): string | undefined {
  if (!cause) {
    return undefined;
  }
  const key = cause.toLowerCase().trim();
  return FAILURE_CAUSE_LABELS[key] ?? humanizeCause(cause);
}

export function labelHangupCause(cause: string | null | undefined): string | undefined {
  if (!cause) {
    return undefined;
  }
  const key = cause.toLowerCase().trim();
  return HANGUP_CAUSE_LABELS[key] ?? humanizeCause(cause);
}

export const CALL_TYPE_LABELS: Record<CallHistoryType, string> = {
  manual: 'Manual',
  campaign: 'Campanha',
  receptive: 'Receptivo',
};

function toNumberOrUndefined(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    return Number(value.trim());
  }
  return undefined;
}

/** Identifica o tipo da chamada a partir do registro bruto. */
export function resolveCallType(
  record: Pick<CallixCallRecord, 'call_type' | 'campaign_id' | 'campaign_contact_id' | 'audio_link' | 'queue_id'>
): CallHistoryType {
  const audio = record.audio_link ?? '';
  const hasCampaign = Boolean(record.campaign_id || record.campaign_contact_id);
  // Dados explícitos (call_type, campanha, caminho da gravação) têm precedência
  // sobre a heurística de fila.
  if (record.call_type === 'manual' || /\/outgoing_/.test(audio)) {
    return 'manual';
  }
  if (hasCampaign || /\/campaign_/.test(audio)) {
    return 'campaign';
  }
  if (/incoming|inbound|receptive/.test(audio) || (record.queue_id !== null && record.queue_id !== undefined)) {
    return 'receptive';
  }
  return 'manual';
}

/**
 * Caminhos de gravação aceitos pelo proxy de áudio
 * (ex.: /api/v1/outgoing_completed_calls/15609870/audio).
 */
export function isAllowedAudioPath(path: string | null | undefined): path is string {
  if (!path || typeof path !== 'string') {
    return false;
  }
  return /^\/api\/v1\/[a-z]+_(?:completed|missed)_calls\/\d+\/audio$/.test(path);
}

function secondsBetween(start: Date | null, end: Date | null): number | undefined {
  if (!start || !end) {
    return undefined;
  }
  const diff = (end.getTime() - start.getTime()) / 1000;
  return diff >= 0 ? Math.round(diff) : undefined;
}

/** Transforma o registro bruto da API no formato consumido pelo widget. */
export function transformCallRecord(record: CallixCallRecord): CallHistoryItem {
  const started = parseCallixTimestamp(record.started_at);
  const answered = parseCallixTimestamp(record.answered_at);
  const ended = parseCallixTimestamp(record.ended_at);

  const callType = resolveCallType(record);
  // Completada quando não há motivo de falha e algum sinal de atendimento existe
  // (answered_at, hangup_cause ou gravação de "_completed_calls").
  const completedByAudio = /_completed_calls\//.test(record.audio_link ?? '');
  const wasAnswered = !record.failure_cause && (Boolean(answered) || Boolean(record.hangup_cause) || completedByAudio);

  // Atendida: tempo de conversa (atendimento até desligar; sem answered_at usa o início).
  // Não atendida: tempo da tentativa.
  const duration = wasAnswered ? secondsBetween(answered ?? started, ended) : secondsBetween(started, ended);

  const item: CallHistoryItem = {
    id: record._id,
    phoneNumber: record.destination_phone ?? '',
    formattedPhone: formatNationalPhone(record.destination_phone ?? ''),
    callType,
    callTypeLabel: CALL_TYPE_LABELS[callType],
    callDirection: callType === 'receptive' ? 'incoming' : 'outgoing',
    wasAnswered,
    startedAt: started ? started.toISOString() : record.started_at,
    formattedDate: started ? formatDateInBrasilia(started) : '--/--/----',
    formattedTime: started ? formatTimeInBrasilia(started) : '--:--',
    formattedTimeWithSeconds: started ? formatTimeInBrasilia(started, true) : '--:--:--',
    dateKey: started ? getDateKeyInBrasilia(started) : '',
    duration,
    formattedDuration: formatDurationSeconds(duration),
    hasAudio: wasAnswered && isAllowedAudioPath(record.audio_link),
  };

  if (answered) item.answeredAt = answered.toISOString();
  if (ended) item.endedAt = ended.toISOString();
  if (record.failure_cause) {
    item.failureCause = record.failure_cause;
    item.failureCauseLabel = labelFailureCause(record.failure_cause);
  }
  if (record.hangup_cause) {
    item.hangupCause = record.hangup_cause;
    item.hangupCauseLabel = labelHangupCause(record.hangup_cause);
  }
  const protocol = toNumberOrUndefined(record.protocol);
  if (protocol !== undefined) item.protocol = protocol;
  const campaignId = toNumberOrUndefined(record.campaign_id);
  if (campaignId !== undefined) item.campaignId = campaignId;
  if (record.campaign_name) item.campaignName = record.campaign_name;
  const campaignContactId = toNumberOrUndefined(record.campaign_contact_id);
  if (campaignContactId !== undefined) item.campaignContactId = campaignContactId;
  if (record.contact_label) item.contactLabel = record.contact_label;
  const qualificationId = toNumberOrUndefined(record.qualification_id);
  if (qualificationId !== undefined) item.qualificationId = qualificationId;
  if (record.qualification_name) item.qualificationName = record.qualification_name;
  if (record.note) item.note = record.note;
  if (isAllowedAudioPath(record.audio_link)) item.audioPath = record.audio_link;
  if (record.fields && typeof record.fields === 'object' && Object.keys(record.fields).length > 0) {
    item.fields = record.fields;
  }

  return item;
}

/** Dígitos do telefone (sem +55) para discar/WhatsApp a partir de um item do histórico. */
export function historyPhoneDigits(item: Pick<CallHistoryItem, 'phoneNumber'>): string {
  const digits = onlyDigits(item.phoneNumber);
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    return digits.slice(2);
  }
  return digits;
}

// ---------------------------------------------------------------------------
// Busca no histórico
// ---------------------------------------------------------------------------

/** Minúsculas, sem acentos, espaços normalizados. */
export function normalizeSearchText(value: string | null | undefined): string {
  return (value ?? '')
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export type HistoryQueryKind = 'empty' | 'phone' | 'text';

export interface HistoryQuery {
  kind: HistoryQueryKind;
  /** Dígitos da consulta (busca por número) */
  digits: string;
  /** Texto normalizado (busca por campanha, cliente, qualificação...) */
  text: string;
}

/**
 * Classifica a consulta: só dígitos/pontuação de telefone com 3+ dígitos é
 * busca por número (pode ir ao servidor); qualquer outra coisa é busca por texto.
 */
export function classifyHistoryQuery(query: string | null | undefined): HistoryQuery {
  const raw = (query ?? '').trim();
  if (!raw) {
    return { kind: 'empty', digits: '', text: '' };
  }
  const digits = onlyDigits(raw);
  const nonPhoneChars = raw.replace(/[\d\s()+\-.]/g, '');
  if (digits.length >= 3 && nonPhoneChars.length === 0) {
    return { kind: 'phone', digits, text: normalizeSearchText(raw) };
  }
  return { kind: 'text', digits, text: normalizeSearchText(raw) };
}

function historySearchHaystack(item: CallHistoryItem): string {
  const parts: Array<string | undefined> = [
    item.formattedPhone,
    historyPhoneDigits(item),
    item.callTypeLabel,
    item.campaignName,
    item.contactLabel,
    item.qualificationName,
    item.failureCauseLabel,
    item.hangupCauseLabel,
    item.formattedDate,
    item.note,
    item.protocol !== undefined ? String(item.protocol) : undefined,
  ];
  if (item.fields) {
    for (const value of Object.values(item.fields)) {
      if (value !== undefined && value !== null) {
        parts.push(String(value));
      }
    }
  }
  return normalizeSearchText(parts.filter(Boolean).join(' | '));
}

/** Um item casa com a consulta: por dígitos (número) ou por todos os termos do texto. */
export function itemMatchesHistoryQuery(item: CallHistoryItem, query: HistoryQuery): boolean {
  if (query.kind === 'empty') {
    return true;
  }
  if (query.kind === 'phone') {
    return (
      historyPhoneDigits(item).includes(query.digits) ||
      onlyDigits(item.phoneNumber).includes(query.digits) ||
      (item.protocol !== undefined && String(item.protocol).includes(query.digits))
    );
  }
  const haystack = historySearchHaystack(item);
  const terms = query.text.split(' ').filter(Boolean);
  return terms.every((term) => haystack.includes(term));
}

export function filterHistoryItems(items: CallHistoryItem[], query: string | HistoryQuery): CallHistoryItem[] {
  const parsed = typeof query === 'string' ? classifyHistoryQuery(query) : query;
  if (parsed.kind === 'empty') {
    return items;
  }
  return items.filter((item) => itemMatchesHistoryQuery(item, parsed));
}
